import { CustomerRegistrationForm, DepartmentResponse } from '../../types';
import { CustomerDetailForm } from '../../types/company.types';
import {
  CreateCustomerSupplierRequest,
  CreateCustomerRequest,
} from '../../services/companies.service';

import { mapRepresentativesToDepartmentContactLists } from './common-mapper';

import { getPartnerClassification } from '@/utils/mapping';
import { CustomerSupplierCategory } from '@/lib/enums';

/**
 * Maps company registration form data to API request format
 * Converts form field names to API field names and IDs to codes
 */
export const mapCustomerFormRequest = (
  formData: CustomerRegistrationForm,
  departments: DepartmentResponse[] = []
): CreateCustomerSupplierRequest => {
  const departmentContactLists = mapRepresentativesToDepartmentContactLists(
    formData.representatives,
    departments
  );

  return {
    galiAccountCode: formData.galiCustomerCode ? Number.parseInt(formData.galiCustomerCode) : null,
    partnerClassification: getPartnerClassification(formData.companyType),
    formalName: formData.officialName,
    abbreviationName: formData.abbreviatedName,
    branchDepartment: formData.branchDepartment || '',
    partnerNameKana: formData.customerNameKatakana || '',
    locationZipCodeFirst: Number.parseInt(formData.currentAddress.postalCode1) || 0,
    locationZipCodeLast: Number.parseInt(formData.currentAddress.postalCode2) || 0,
    locationAddress1: formData.currentAddress.address1 || '',
    locationAddress2: formData.currentAddress.address2 || '',
    locationAddress3: formData.currentAddress.address3 || '',
    locationPhone: formData.currentAddress.phone || '',
    locationFax: formData.currentAddress.fax || '',
    headquartersAddressZipCodeFirst: Number.parseInt(formData.headOfficeAddress.postalCode1) || 0,
    headquartersAddressZipCodeLast: Number.parseInt(formData.headOfficeAddress.postalCode2) || 0,
    headquartersAddress1: formData.headOfficeAddress.address1 || '',
    headquartersAddress2: formData.headOfficeAddress.address2 || '',
    headquartersAddress3: formData.headOfficeAddress.address3 || '',
    departmentContactLists,
    customerCategory: formData.customerCategory || CustomerSupplierCategory.GENERAL_CONTRACTORS,
  };
};

/**
 * Maps customer detail form data to API request format for editing
 * Converts form field names to API field names and IDs to codes
 */
export const mappingEditCustomerData = (
  formData: CustomerDetailForm,
  departments: DepartmentResponse[] = []
): CreateCustomerRequest => {
  const departmentContactLists = mapRepresentativesToDepartmentContactLists(
    formData.representatives,
    departments
  );

  return {
    galiAccountCode: formData.galiAccountCode ? Number.parseInt(formData.galiAccountCode) : null,
    partnerClassification: 1, // Customer
    formalName: formData.formalName,
    abbreviationName: formData.abbreviationName,
    branchDepartment: formData.branchDepartment || '',
    partnerNameKana: formData.partnerNameKana || '',
    locationZipCodeFirst: Number.parseInt(formData.currentAddress.postalCode1) || 0,
    locationZipCodeLast: Number.parseInt(formData.currentAddress.postalCode2) || 0,
    locationAddress1: formData.currentAddress.address1 || '',
    locationAddress2: formData.currentAddress.address2 || '',
    locationAddress3: formData.currentAddress.address3 || '',
    locationPhone: formData.currentAddress.phone || '',
    locationFax: formData.currentAddress.fax || '',
    headquartersAddressZipCodeFirst: Number.parseInt(formData.headOfficeAddress.postalCode1) || 0,
    headquartersAddressZipCodeLast: Number.parseInt(formData.headOfficeAddress.postalCode2) || 0,
    headquartersAddress1: formData.headOfficeAddress.address1 || '',
    headquartersAddress2: formData.headOfficeAddress.address2 || '',
    headquartersAddress3: formData.headOfficeAddress.address3 || '',
    departmentContactLists,
    customerCategory: (() => {
      // Ensure valid customer category (1-5)
      const category = formData.customerCategory;
      if (category && category >= 1 && category <= 5) {
        return category;
      }
      return 1; // Default to GENERAL_CONTRACTOR
    })(),
  };
};

/**
 * Maps customer detail form data to API request format for editing (with data cleaning)
 */
export const mappingEditCustomerDataCleaned = (
  formData: CustomerDetailForm,
  departments: DepartmentResponse[] = []
): CreateCustomerRequest => {
  const rawData = mappingEditCustomerData(formData, departments);
  return cleanApiRequest(rawData) as CreateCustomerRequest;
};

/**
 * Checks if an array item should be kept after cleaning
 */
const shouldKeepArrayItem = (item: unknown): boolean => {
  if (typeof item === 'object' && item !== null) {
    return Object.keys(item).length > 0;
  }
  return item != null && item !== '';
};

/**
 * Cleans an array by recursively cleaning items and filtering out empty ones
 */
const cleanArray = (arr: unknown[]): unknown[] => {
  return arr.map(cleanApiRequest).filter(shouldKeepArrayItem);
};

/**
 * Cleans a string value by trimming whitespace
 */
const cleanStringValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  return trimmed;
};

/**
 * Cleans an object value and returns it if not empty
 */
const cleanObjectValue = (value: unknown): unknown => {
  const cleanedObject = cleanApiRequest(value);
  if (typeof cleanedObject === 'object' && cleanedObject !== null) {
    return Object.keys(cleanedObject).length > 0 ? cleanedObject : null;
  }
  return null;
};

/**
 * Processes a single object entry and returns the cleaned value
 */
const processObjectEntry = (value: unknown): unknown => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    return cleanStringValue(value);
  }

  if (Array.isArray(value)) {
    const cleanedArray = cleanArray(value);
    return cleanedArray.length > 0 ? cleanedArray : null;
  }

  if (typeof value === 'object') {
    return cleanObjectValue(value);
  }

  // Include numbers, booleans, etc.
  return value;
};

/**
 * Cleans an object by processing all entries and removing empty values
 */
const cleanObject = (data: Record<string, unknown>): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const cleanedValue = processObjectEntry(value);
    if (cleanedValue !== null) {
      cleaned[key] = cleanedValue;
    }
  }

  return cleaned;
};

/**
 * Helper function to clean API request data
 * Removes empty strings, null values, and empty arrays that might cause backend validation errors
 */
const cleanApiRequest = (data: unknown): unknown => {
  if (Array.isArray(data)) {
    return cleanArray(data);
  }

  if (typeof data === 'object' && data !== null) {
    return cleanObject(data as Record<string, unknown>);
  }

  return data;
};
